// Gestão de Usuários
import { useState, useEffect } from 'react';
import { Card } from '../../../components/common/Card';
import { Button } from '../../../components/common/Button';
import { Input } from '../../../components/common/Input';
import { Select } from '../../../components/common/Select';
import { Table } from '../../../components/common/Table';
import { Modal } from '../../../components/common/Modal';
import { Badge } from '../../../components/common/Badge';
import { Loading } from '../../../components/common/Loading';
import { Breadcrumbs } from '../../../components/layout/Breadcrumbs';
import { Plus, Edit, Trash2, AlertCircle, X, Users } from 'lucide-react';
import { formatDate } from '../../../utils/formatters';
import { obterUsuariosComEmpresas } from '../../../services/adminService';
import { supabase } from '../../../lib/supabase';
import { vincularUsuarioEmpresa, desvincularUsuarioEmpresa } from '../../../services/usuariosService';
import { getEmpresas } from '../../../services/empresasService';

interface Usuario {
  usuarioId: string;
  id?: string; // Para compatibilidade
  nome: string;
  email: string;
  totalEmpresas: number;
  empresas: Array<{
    id: string;
    razaoSocial: string;
    nomeFantasia: string;
    cnpj: string;
    role: string;
  }>;
}

export function Usuarios() {
  const [busca, setBusca] = useState('');
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [empresas, setEmpresas] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [usuarioSelecionado, setUsuarioSelecionado] = useState<Usuario | null>(null);
  const [empresaParaVincular, setEmpresaParaVincular] = useState('');
  const [roleParaVincular, setRoleParaVincular] = useState<'admin' | 'operador' | 'visualizador'>('visualizador');
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    nome: '',
    confirmPassword: '',
  });

  useEffect(() => {
    carregarUsuarios();
    carregarEmpresas();
  }, []);

  const carregarUsuarios = async () => {
    try {
      setLoading(true);
      setError(null);
      const usuariosData = await obterUsuariosComEmpresas();
      setUsuarios(usuariosData);
    } catch (error) {
      console.error('[Usuarios] Erro ao carregar usuários:', error);
      setError('Erro ao carregar usuários');
    } finally {
      setLoading(false);
    }
  };

  const carregarEmpresas = async () => {
    try {
      const empresasData = await getEmpresas();
      setEmpresas(empresasData);
    } catch (error) {
      console.error('[Usuarios] Erro ao carregar empresas:', error);
    }
  };

  const handleVincularEmpresa = async () => {
    if (!usuarioSelecionado || !empresaParaVincular) {
      setError('Selecione uma empresa para vincular');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await vincularUsuarioEmpresa(usuarioSelecionado.usuarioId, empresaParaVincular, roleParaVincular);

      setEmpresaParaVincular('');
      setRoleParaVincular('visualizador');
      await carregarUsuarios();
    } catch (err: any) {
      console.error('[Usuarios] Erro ao vincular empresa:', err);
      setError(err.message || 'Erro ao vincular empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDesvincularEmpresa = async (empresaId: string) => {
    if (!usuarioSelecionado) return;

    if (!confirm('Tem certeza que deseja desvincular esta empresa do usuário?')) {
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      await desvincularUsuarioEmpresa(usuarioSelecionado.usuarioId, empresaId);

      await carregarUsuarios();
    } catch (err: any) {
      console.error('[Usuarios] Erro ao desvincular empresa:', err);
      setError(err.message || 'Erro ao desvincular empresa');
    } finally {
      setIsSaving(false);
    }
  };

  const usuariosFiltrados = usuarios.filter((u) => {
    const matchBusca = !busca || 
      u.nome.toLowerCase().includes(busca.toLowerCase()) || 
      u.email.toLowerCase().includes(busca.toLowerCase());
    return matchBusca;
  });

  const abrirModal = (usuario?: Usuario) => {
    if (usuario) {
      setUsuarioSelecionado(usuario);
      setFormData({
        email: usuario.email,
        password: '',
        nome: usuario.nome,
        confirmPassword: '',
      });
    } else {
      setUsuarioSelecionado(null);
      setFormData({
        email: '',
        password: '',
        nome: '',
        confirmPassword: '',
      });
    }
    setIsModalOpen(true);
  };

  const handleCriarUsuario = async () => {
    try {
      if (!formData.email || !formData.password) {
        setError('Email e senha são obrigatórios');
        return;
      }

      if (formData.password !== formData.confirmPassword) {
        setError('As senhas não coincidem');
        return;
      }

      if (formData.password.length < 6) {
        setError('A senha deve ter pelo menos 6 caracteres');
        return;
      }

      setIsSaving(true);
      setError(null);

      // Validar formato de email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError('Email inválido. Use um formato válido como exemplo@dominio.com');
        return;
      }

      // Criar usuário no Supabase Auth
      const { data, error: signUpError } = await supabase.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            nome: formData.nome || formData.email.split('@')[0],
          },
          emailRedirectTo: undefined, // Não redirecionar email
        },
      });

      if (signUpError) {
        // Tratar erros específicos do Supabase
        if (signUpError.message.includes('already registered')) {
          throw new Error('Este email já está cadastrado');
        } else if (signUpError.message.includes('invalid')) {
          throw new Error('Email inválido. Verifique o formato do email');
        } else {
          throw signUpError;
        }
      }

      if (!data.user) {
        throw new Error('Usuário não foi criado');
      }

      // Limpar formulário
      setFormData({
        email: '',
        password: '',
        nome: '',
        confirmPassword: '',
      });

      setIsModalOpen(false);
      await carregarUsuarios();
    } catch (err: any) {
      console.error('[Usuarios] Erro ao criar usuário:', err);
      setError(err.message || 'Erro ao criar usuário');
    } finally {
      setIsSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loading />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: 'Admin', path: '/app/admin' }, { label: 'Usuários' }]} />
      
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Gestão de Usuários</h1>
          <p className="text-gray-600 dark:text-gray-400">Gerenciar usuários da plataforma</p>
        </div>
        <Button variant="primary" onClick={() => abrirModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Novo Usuário
        </Button>
      </div>

      {error && (
        <Card>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
            <AlertCircle className="w-5 h-5" />
            <span>{error}</span>
          </div>
        </Card>
      )}

      {/* Filtros */}
      <Card>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            label="Buscar"
            placeholder="Nome, email..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
        </div>
      </Card>

      {/* Tabela */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Usuários ({usuariosFiltrados.length})
        </h2>
        {usuariosFiltrados.length === 0 ? (
          <div className="text-center py-12 text-gray-500 dark:text-gray-400">
            Nenhum usuário encontrado
          </div>
        ) : (
          <Table
            data={usuariosFiltrados}
            columns={[
              { key: 'nome', label: 'Nome' },
              { key: 'email', label: 'Email' },
              { 
                key: 'totalEmpresas', 
                label: 'Empresas',
                render: (item) => (
                  <Badge variant="default">{item.totalEmpresas}</Badge>
                ),
              },
              {
                key: 'empresas',
                label: 'Empresas Vinculadas',
                render: (item) => (
                  <div className="max-w-xs">
                    {item.empresas.length === 0 ? (
                      <span className="text-gray-400">Nenhuma</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {item.empresas.slice(0, 2).map((empresa) => (
                          <Badge key={empresa.id} variant="default" className="text-xs">
                            {empresa.nomeFantasia || empresa.razaoSocial}
                          </Badge>
                        ))}
                        {item.empresas.length > 2 && (
                          <Badge variant="default" className="text-xs">
                            +{item.empresas.length - 2}
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ),
              },
              {
                key: 'actions',
                label: 'Ações',
                render: (item) => (
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => abrirModal(item)}
                      className="p-2 text-primary-600 hover:bg-primary-50 dark:hover:bg-primary-900/20 rounded-lg"
                      title="Ver detalhes"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {/* Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={usuarioSelecionado ? 'Detalhes do Usuário' : 'Novo Usuário'}
        size="lg"
      >
        <div className="space-y-4">
          {usuarioSelecionado ? (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Nome
                </label>
                <p className="text-gray-900 dark:text-gray-100">{usuarioSelecionado.nome || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <p className="text-gray-900 dark:text-gray-100">{usuarioSelecionado.email || '-'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Total de Empresas
                </label>
                <p className="text-gray-900 dark:text-gray-100">{usuarioSelecionado.totalEmpresas || 0}</p>
              </div>
              <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Empresas Vinculadas ({usuarioSelecionado.empresas.length})
                </label>
                {usuarioSelecionado.empresas.length > 0 ? (
                  <div className="space-y-2 mb-4">
                    {usuarioSelecionado.empresas.map((empresa) => (
                      <div key={empresa.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium text-gray-900 dark:text-gray-100">
                            {empresa.nomeFantasia || empresa.razaoSocial}
                          </p>
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {empresa.cnpj} • Role: {empresa.role}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDesvincularEmpresa(empresa.id)}
                          disabled={isSaving}
                          className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Desvincular empresa"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">Nenhuma empresa vinculada</p>
                )}

                {/* Seção para vincular nova empresa */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    Vincular Nova Empresa
                  </label>
                  <div className="space-y-3">
                    <Select
                      label="Empresa"
                      value={empresaParaVincular}
                      onChange={(e) => setEmpresaParaVincular(e.target.value)}
                      options={[
                        { value: '', label: 'Selecione uma empresa' },
                        ...empresas
                          .filter(e => !usuarioSelecionado.empresas.find(ue => ue.id === e.id))
                          .map((e) => ({
                            value: e.id,
                            label: `${e.nomeFantasia} (${e.cnpj})`,
                          })),
                      ]}
                    />
                    <Select
                      label="Role"
                      value={roleParaVincular}
                      onChange={(e) => setRoleParaVincular(e.target.value as 'admin' | 'operador' | 'visualizador')}
                      options={[
                        { value: 'admin', label: 'Admin' },
                        { value: 'operador', label: 'Operador' },
                        { value: 'visualizador', label: 'Visualizador' },
                      ]}
                    />
                    <Button
                      variant="primary"
                      onClick={handleVincularEmpresa}
                      disabled={!empresaParaVincular || isSaving}
                      className="w-full"
                    >
                      <Users className="w-4 h-4 mr-2" />
                      Vincular Empresa
                    </Button>
                  </div>
                </div>
              </div>
              <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={() => {
                  setIsModalOpen(false);
                  setEmpresaParaVincular('');
                  setRoleParaVincular('visualizador');
                }}>
                  Fechar
                </Button>
              </div>
            </>
          ) : (
            <>
              <Input
                label="Nome"
                value={formData.nome}
                onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                placeholder="Nome do usuário"
              />
              <Input
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@exemplo.com"
                required
              />
              <Input
                label="Senha"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="Mínimo 6 caracteres"
                required
              />
              <Input
                label="Confirmar Senha"
                type="password"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                placeholder="Digite a senha novamente"
                required
              />
              {error && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center gap-2 text-red-700 dark:text-red-400">
                  <AlertCircle className="w-5 h-5" />
                  <span>{error}</span>
                </div>
              )}
              <div className="flex gap-4 justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="secondary" onClick={() => setIsModalOpen(false)} disabled={isSaving}>
                  Cancelar
                </Button>
                <Button variant="primary" onClick={handleCriarUsuario} disabled={isSaving}>
                  {isSaving ? 'Criando...' : 'Criar Usuário'}
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </div>
  );
}
